import {useFeatureFlagStore} from '@/stores/featureFlagStore';
import {useAccountStore} from '@/stores/accountStore';
import {appConfig} from '@/config/appConfig';
import router from '@/router/router';
import type AccountForm from '../../api/models/form/AccountForm';

/**
 * Check if user should be redirected to their custom subdomain
 * @param targetRoute - Optional target route after redirect
 * @returns Promise<boolean> - true if redirect was triggered, false otherwise
 */
export async function checkAndRedirect(targetRoute?: {name: string} | string): Promise<boolean> {
    // Skip redirect for hybrid apps and Zrok environments
    if (__IS_HYBRID_APP__ || appConfig.isZrokShare) {
        return false;
    }

    // Skip redirect 

    const featureFlagStore = useFeatureFlagStore();

    const isReady = await featureFlagStore.subdomainRedirect.isReady();

    // Check if subdomain redirect feature is enabled
    if (!isReady) {
        return false;
    }

    const accountStore = useAccountStore();
    const account = accountStore.account;

    const regex = /^(https:\/\/)(\w+)(\.)/i;
    const parts = regex.exec(window.location.origin);

    if (!parts) {
        return false;
    }

    const currentSubdomain = parts[2];
    // Redirect to 'app' for nonsense domains
    const customSubdomain = account?.customSubdomain || 'app';

    // If subdomains match, no redirect needed
    if (currentSubdomain === customSubdomain) {
        return false;
    }

    // Build redirect URL
    let redirectUrl = window.location.origin;

    if (targetRoute) {
        const route = typeof targetRoute === 'string' ? {name: targetRoute} : targetRoute;
        redirectUrl += router.resolve(route).href;
    } else {
        redirectUrl += window.location.pathname + window.location.search;
    }

    // Replace subdomain in URL
    const newUrl = redirectUrl.replace(regex, '$1' + customSubdomain + '$3');

    // Perform redirect
    window.location.href = newUrl;

    return true;
}

/**
 * Check subdomain redirect with tokens (for login flow)
 * @param token - Access token
 * @param refreshToken - Refresh token
 * @param targetRoute - Optional target route
 * @param accountData - Account data from login response (since store might not be updated yet)
 */
export function checkAndRedirectWithTokens(
    token: string,
    refreshToken: string,
    targetRoute?: {name: string} | string,
    accountData?: AccountForm
): boolean {
    // Skip redirect for hybrid apps and Zrok environments
    if (__IS_HYBRID_APP__ || appConfig.isZrokShare) {
        return false;
    }

    // Use provided account data or fall back to store
    const account = accountData || useAccountStore().account;

    const hostname = window.location.hostname;
    const currentSubdomain = hostname.split('.').shift();
    // Redirect to 'app' for nonsense domains
    const customSubdomain = account?.customSubdomain || 'app';

    if (currentSubdomain === customSubdomain) {
        return false;
    }

    // Build redirect URL with tokens
    const origin = window.location.origin;
    const newOrigin = origin.replace(
        hostname,
        hostname.replace(currentSubdomain!, customSubdomain)
    );

    const searchParams = new URLSearchParams();
    searchParams.append('token', token);
    searchParams.append('refreshToken', refreshToken);

    let path = '/login';
    if (targetRoute) {
        const route = typeof targetRoute === 'string' ? {name: targetRoute} : targetRoute;
        path = router.resolve(route).href;
    }

    window.location.href = `${newOrigin}${path}?${searchParams.toString()}`;

    return true;
}
